import { EditorState, Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import 'prosemirror-view/style/prosemirror.css';
import 'prosemirror-gapcursor/style/gapcursor.css';
import 'prosemirror-tables/style/tables.css';
import { baseKeymap } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';
import { tableEditing, columnResizing, fixTables } from 'prosemirror-tables';
import { ySyncPlugin, yUndoPlugin, undo, redo } from 'y-prosemirror';
import * as Y from 'yjs';

import { schema } from './schema';
import { taskPlugin, TASK_TOGGLE_META } from './plugins/task-plugin';
import { multiplierPlugin } from './plugins/multiplier-plugin';
import { lockedModePlugin } from './plugins/locked-mode-plugin';
import { headingPlugin } from './plugins/heading-plugin';
import { createKeymapPlugin, createInputRulesPlugin } from './plugins/keymap';
import { YjsSync } from './sync/yjs-sync';
import { createToolbar } from './toolbar';
import { DocumentId } from '@kafka/shared';
import { NumberNodeView } from './views/number-node-view';
import { TaskNodeView } from './views/task-node-view';

function createPlugins(yXmlFragment: Y.XmlFragment, locked: boolean): Plugin[] {
  return [
    ySyncPlugin(yXmlFragment),
    yUndoPlugin(),
    keymap({
      'Mod-z': undo,
      'Mod-y': redo,
      'Mod-Shift-z': redo,
    }),
    createInputRulesPlugin(),
    createKeymapPlugin(),
    keymap(baseKeymap),
    taskPlugin(),
    multiplierPlugin(),
    lockedModePlugin(locked),
    headingPlugin(),
    columnResizing(),
    tableEditing(),
    new Plugin({
      appendTransaction(_, oldState, newState) {
        // Fix malformed tables
        let tr = fixTables(newState);

        // Ensure paragraph after tables at document end (prevents "trapped in table")
        const lastChild = newState.doc.lastChild;
        if (lastChild?.type.name === 'table') {
          const insertPos = newState.doc.content.size;
          tr = tr || newState.tr;
          tr.insert(insertPos, schema.nodes.paragraph.create());
        }

        return tr || undefined;
      },
    }),
    dropCursor(),
    gapCursor(),
  ];
}

export interface EditorOptions {
  locked?: boolean;
}

export interface EditorInstance {
  view: EditorView;
  sync: YjsSync;
  destroy: () => void;
}

export function createEditor(
  container: HTMLElement,
  documentId: DocumentId,
  options: EditorOptions = {}
): EditorInstance {
  const sync = new YjsSync(documentId);
  const locked = options.locked ?? false;
  const yXmlFragment = sync.doc.getXmlFragment('prosemirror');

  const state = EditorState.create({
    schema,
    plugins: createPlugins(yXmlFragment, locked),
  });

  // Create editor container
  const editorContent = document.createElement('div');
  editorContent.className = 'editor-content';

  // Toolbar update function (set after toolbar creation)
  let toolbarUpdate: (() => void) | null = null;

  const view = new EditorView(editorContent, {
    state,
    nodeViews: {
      numberNode(node, view, getPos) {
        return new NumberNodeView(node, view, getPos);
      },
      task(node, view, getPos) {
        return new TaskNodeView(node, view, getPos);
      },
    },
    dispatchTransaction(this: EditorView, tr) {
      const newState = this.state.apply(tr);
      this.updateState(newState);
      // Scroll cursor into view when document changes (but not for task toggles)
      if ((tr.docChanged || tr.scrolledIntoView) && !tr.getMeta(TASK_TOGGLE_META)) {
        const editorView = this;
        requestAnimationFrame(() => {
          const { from } = editorView.state.selection;
          try {
            const domAtPos = editorView.domAtPos(from);
            const node = domAtPos.node instanceof Element
              ? domAtPos.node
              : domAtPos.node.parentElement;
            node?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
          } catch {
            // Ignore if position is invalid
          }
        });
      }
      // Update toolbar active states
      toolbarUpdate?.();
    },
  });

  // Add toolbars and editor (loading indicator hidden via CSS until doc loads)
  const toolbar = createToolbar(view, sync);
  toolbarUpdate = toolbar.update;
  container.appendChild(toolbar.element);
  container.appendChild(toolbar.secondaryToolbar);
  container.appendChild(editorContent);

  // Handle document reset from server (e.g., when validation fails)
  sync.onReset((newDoc: Y.Doc) => {
    const newFragment = newDoc.getXmlFragment('prosemirror');
    const newState = EditorState.create({
      schema,
      plugins: createPlugins(newFragment, locked),
    });
    view.updateState(newState);
    toolbarUpdate?.();
  });

  // Show editor when initial sync completes
  sync.onInitialSyncComplete(() => {
    const editor = document.getElementById('editor');
    if (editor?.classList.contains('loading')) {
      document.getElementById('loading')?.remove();
      editor.classList.remove('loading');
    }
  });

  // Connect sync
  sync.connect();

  return {
    view,
    sync,
    destroy() {
      sync.disconnect();
      view.destroy();
    },
  };
}
